import type { ConditionModel } from '@ctw/shared/api/fhir/models/condition';
import type { DiagnosticReportModel } from '@ctw/shared/api/fhir/models/diagnostic-report';
import type { DocumentModel } from '@ctw/shared/api/fhir/models/document';
import type { ObservationModel } from '@ctw/shared/api/fhir/models/observation';
import type { RiskGapEvidenceReference } from '@ctw/shared/api/fhir/models/risk-gap';
import { useResources } from '@ctw/shared/context/_internal/resource/resource-fetch';
import { usePatientContextIfAvailable } from '@ctw/shared/context/patient-provider';
import { tw } from '@ctw/shared/utils/tailwind';
import { useMemo } from 'react';

interface ClinicalEvidenceCardProps {
  reference: RiskGapEvidenceReference;
}

interface ClinicalEvidenceCardContent {
  title: string;
  primary: string;
  secondary: string;
  date?: string;
}

type SupportedEvidenceResourceType =
  | 'Condition'
  | 'Observation'
  | 'DiagnosticReport'
  | 'DocumentReference';

type SupportedEvidenceModel =
  | ConditionModel
  | ObservationModel
  | DiagnosticReportModel
  | DocumentModel;

interface ClinicalEvidenceHandler<TModel extends SupportedEvidenceModel> {
  resourceType: SupportedEvidenceResourceType;
  toCardContent: (model: TModel | undefined) => ClinicalEvidenceCardContent;
}

const CLINICAL_EVIDENCE_HANDLERS: Record<
  SupportedEvidenceResourceType,
  ClinicalEvidenceHandler<SupportedEvidenceModel>
> = {
  Condition: {
    resourceType: 'Condition',
    toCardContent: (model) => {
      const condition = model as ConditionModel | undefined;
      return {
        title: condition?.isEncounterDiagnosis ? 'Encounter Diagnosis' : 'Condition',
        primary: condition?.icd10CMEnrichments[0]?.code || condition?.display || 'Unknown',
        secondary: condition?.display || 'Condition',
        date: condition?.recordedDate ? `Last documented ${condition.recordedDate}` : undefined,
      };
    },
  },
  Observation: {
    resourceType: 'Observation',
    toCardContent: (model) => {
      const observation = model as ObservationModel | undefined;
      return {
        title: 'Observation',
        primary: observation?.valueWithUnits || 'Unknown result',
        secondary: observation?.title || 'Observation',
        date: observation?.dateDisplay ? `Captured ${observation.dateDisplay}` : 'Captured unknown',
      };
    },
  },
  DiagnosticReport: {
    resourceType: 'DiagnosticReport',
    toCardContent: (model) => {
      const diagnosticReport = model as DiagnosticReportModel | undefined;
      return {
        title: 'Diagnostic Report',
        primary: diagnosticReport?.displayName || 'Diagnostic report',
        secondary:
          diagnosticReport?.performer ||
          diagnosticReport?.dataSource ||
          diagnosticReport?.numResultsDisplay ||
          'Diagnostic report',
        date: diagnosticReport?.dateDisplay
          ? `Reported ${diagnosticReport.dateDisplay}`
          : undefined,
      };
    },
  },
  DocumentReference: {
    resourceType: 'DocumentReference',
    toCardContent: (model) => {
      const document = model as DocumentModel | undefined;
      return {
        title: 'Document',
        primary: document?.title || 'Document',
        secondary: document?.typeDisplay || document?.custodian || 'Document',
        date: document?.encounterDate
          ? `Encounter ${document.encounterDate}`
          : document?.dateCreated
            ? `Created ${document.dateCreated}`
            : undefined,
      };
    },
  },
};

export const ClinicalEvidenceCard = ({ reference }: ClinicalEvidenceCardProps) => {
  const patientContext = usePatientContextIfAvailable();
  const handler =
    CLINICAL_EVIDENCE_HANDLERS[reference.resourceType as SupportedEvidenceResourceType];
  const identifiers = useMemo(
    () =>
      handler ? [{ resourceId: reference.resourceId, resourceType: handler.resourceType }] : [],
    [handler, reference.resourceId],
  );
  const { data: resources = [], isLoading } = useResources<SupportedEvidenceModel>({
    identifiers,
    patient: patientContext?.patient ?? ({} as never),
  });

  const content = handler
    ? handler.toCardContent(resources[0])
    : {
        title: reference.resourceType,
        primary: reference.resourceId,
        secondary: reference.resourceType,
      };

  return (
    <div
      className={tw`group relative flex min-w-[180px] flex-1 cursor-pointer flex-col rounded-lg border border-border bg-card p-3 transition-all hover:border-border/80 hover:shadow-sm`}
    >
      <div className={tw`mb-2 flex min-h-[16px] items-center justify-between`}>
        <div className={tw`font-semibold text-muted-foreground text-xs uppercase tracking-wider`}>
          {content.title}
        </div>
      </div>
      <div className={tw`flex flex-1 flex-col`}>
        <div className={tw`break-words font-semibold text-foreground text-lg leading-tight`}>
          {isLoading ? 'Loading...' : content.primary}
        </div>
        <div className={tw`mt-0.5 break-words text-muted-foreground text-xs`}>
          {isLoading ? 'Loading details...' : content.secondary}
        </div>
        <div className={tw`flex-1`} />
        {!isLoading && content.date && (
          <div className={tw`mt-auto pt-2 text-slate-500 text-xs`}>{content.date}</div>
        )}
      </div>
    </div>
  );
};
